import { ReactElement, useRef, useState } from 'react';

import {
  Avatar,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  ChakraToolTip,
  EquipUIFireflyV2Theme,
  EquipUITheme,
} from '@equip.health/ui';

import { ActivityField, NULL_USER_NAME } from '~/lib/constants/task.constants';
import {
  formatSubtext,
  formatValue,
  getTaskActivityIcon,
  replaceNullProvider,
  taskPriorityDefinitions,
  taskStatusDefinitions,
} from '~/lib/util/task.util';
import { DateTime } from 'luxon';
import ProviderAvatarBlockTooltip from '~/components/common/ProviderAvatarBlockTooltip';

const { neutral } = EquipUIFireflyV2Theme.colors;
const { label, labelBold, paragraph } = EquipUITheme.typography;
export const ACTIVITY_LOG_ENTRY_TOP_LEVEL_ID = 'LogEntry';

type LogEntryProps = {
  Icon?: ({ width }: { width: string }) => ReactElement;
  secondaryText?: string;
  subtitle: string;
  title: string;
  updatedBy?: string;
  avatarUrl?: string;
  commentedUserExternalId?: string;
};

export const LogEntry = ({
  avatarUrl,
  commentedUserExternalId,
  Icon,
  secondaryText,
  subtitle,
  title,
  updatedBy,
}: LogEntryProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const nameRef = useRef<HTMLDivElement>(null);

  const handleNameMouseEnter = () => {
    setIsTooltipOpen(true);
  };

  const handleNameMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!tooltipRef.current?.contains(e.relatedTarget as Node)) {
      setIsTooltipOpen(false);
    }
  };

  const handleTooltipMouseEnter = () => {
    setIsTooltipOpen(true);
  };

  const handleTooltipMouseLeave = () => {
    setIsTooltipOpen(false);
  };

  const renderIconOrAvatar = () => {
    if (Icon) {
      return (
        <ChakraFlex alignItems="center" justifyContent="center" width="44px">
          <Icon width="24px" />
        </ChakraFlex>
      );
    } else {
      return (
        <ChakraBox pointerEvents="auto">
          <ChakraToolTip
            bg="white"
            borderRadius="8px"
            closeOnPointerDown={false}
            color="neutral.secondary"
            isOpen={isTooltipOpen}
            label={
              <ChakraBox
                onMouseEnter={handleTooltipMouseEnter}
                onMouseLeave={handleTooltipMouseLeave}
                pointerEvents="auto"
                ref={tooltipRef}
              >
                <ProviderAvatarBlockTooltip
                  providerExternalId={commentedUserExternalId}
                  TOP_LEVEL_ID="task-comment-avatar-tooltip"
                />
              </ChakraBox>
            }
            padding="12px"
            shouldWrapChildren
          >
            <ChakraBox
              onMouseEnter={handleNameMouseEnter}
              onMouseLeave={handleNameMouseLeave}
              ref={nameRef}
            >
              <Avatar
                name={title}
                size="sm"
                src={avatarUrl}
                userType="provider"
              />
            </ChakraBox>
          </ChakraToolTip>
        </ChakraBox>
      );
    }
  };
  return (
    <ChakraFlex
      alignItems="center"
      data-testid={ACTIVITY_LOG_ENTRY_TOP_LEVEL_ID}
    >
      {renderIconOrAvatar()}
      <ChakraFlex flexDirection="column" paddingLeft="8px" width="100%">
        <ChakraFlex>
          <ChakraText {...labelBold} color="neutral.primary">
            {title}
          </ChakraText>
          <ChakraText {...label} color="neutral.secondary" marginLeft="16px">
            {subtitle}
          </ChakraText>
        </ChakraFlex>
        <ChakraBox>
          <ChakraText
            id="secondaryText"
            {...paragraph}
            color="neutral.secondary"
            margin="0 4px 0 0!important"
          >
            {secondaryText}
            <span style={{ fontWeight: 700 }}>{updatedBy}</span>
          </ChakraText>
        </ChakraBox>
      </ChakraFlex>
    </ChakraFlex>
  );
};

export type ParsedActivityEvent = {
  activityExternalId: string;
  fieldChanged: ActivityField;
  newValue: string;
  previousValue?: string;
  statusId?: string;
  updatedAt: string;
  updatedBy: string;
  voidReason?: string;
};

const ActivityLogEntry = ({
  fieldChanged,
  newValue,
  previousValue,
  statusId,
  updatedAt,
  updatedBy,
  voidReason,
}: ParsedActivityEvent) => {
  const { title: status } =
    taskStatusDefinitions.find((def) => def.value === statusId) ?? {};
  const Icon = getTaskActivityIcon(fieldChanged, newValue);
  const title = formatValue(
    fieldChanged === ActivityField.CLOSED_OR_VOID_REASON ? status : newValue,
    fieldChanged,
    true,
  );
  const [secondaryText, updatedByName] =
    fieldChanged === ActivityField.CREATED &&
    (updatedBy === NULL_USER_NAME || !updatedBy)
      ? [undefined, undefined]
      : [
          `${formatSubtext({
            fieldChanged,
            newValue,
            previousValue,
            voidReason,
          })} by `,
          replaceNullProvider(updatedBy),
        ];
  // above call to replaceNullProvider should not be needed after BE fix
  const iconColor =
    fieldChanged === ActivityField.PRIORITY_ID
      ? taskPriorityDefinitions[Number.parseInt(newValue, 10) - 1].color
      : neutral.secondary;
  return (
    <LogEntry
      Icon={({ width }) => (
        <Icon fill={iconColor} fillOpacity={1} height={width} width={width} />
      )}
      secondaryText={secondaryText}
      subtitle={DateTime.fromJSDate(new Date(updatedAt)).toFormat(
        'LL/dd/yyyy h:mm a',
      )}
      title={title}
      updatedBy={updatedByName}
    />
  );
};

export default ActivityLogEntry;
